import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {useContacts} from "@/app/user/messages/hooks/use-contacts.ts";
import {AppointmentChatService} from "@/services/appointment-chat.service.ts";
import {toast} from "sonner";
import {uuid} from "@/interfaces";
import {failureMessage} from "@/repositories/property-people-records";

interface Props { 
    open: boolean; 
    onOpenChange: (o: boolean) => void; 
    roomId: uuid;
    currentUsers: uuid[];
}

export default function AddToGroupModal({ open, onOpenChange, roomId, currentUsers }: Props) {
    const { contacts, loading } = useContacts();
    const [search, setSearch] = useState('');
    const [adding, setAdding] = useState(false);

    // Filter out users who are already in the group chat
    const availableContacts = contacts.filter(c => !currentUsers.includes(c.id));
    
    const filtered = availableContacts.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase())
    );

    const handleAddUser = async (userId: uuid, userName: string) => {
        setAdding(true);
        try {
            const added = await AppointmentChatService.addMember(roomId, userId);
            if (added) {
                toast.success(`${userName} added to group chat`);
            } else {
                toast.info('No changes were made.');
            }
            onOpenChange(false);
            setSearch('');
        } catch (error) {
            console.error('Failed to add user to group chat:', error);
            toast.error(failureMessage(error, 'Failed to add user to group chat'));
        } finally {
            setAdding(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Add to group chat</DialogTitle>
                </DialogHeader>

                <Input
                    placeholder="Search people…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="mb-4"
                />

                <div className="max-h-72 overflow-y-auto space-y-2">
                    {loading && <p className="text-sm text-muted-foreground px-2">Loading…</p>}
                    {!loading && filtered.map(c => (
                        <Button
                            key={c.id}
                            variant="ghost"
                            className="w-full justify-start"
                            disabled={adding}
                            onClick={() => handleAddUser(c.id, c.name)}
                        >
                            <Avatar className="mr-3 h-7 w-7">
                                <AvatarImage src={c.avatar ?? undefined} />
                                <AvatarFallback>{c.name.slice(0,2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <span className="truncate">{c.name}</span>
                        </Button>
                    ))}
                    {!loading && filtered.length === 0 && !availableContacts.length && (
                        <p className="text-sm text-muted-foreground px-2">All contacts are already in this group chat.</p>
                    )}
                    {!loading && availableContacts.length > 0 && filtered.length === 0 && search && (
                        <p className="text-sm text-muted-foreground px-2">No contacts found.</p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
